/* James Crossing — wallpaper roll estimator. Reuses the theme's <modal-dialog> and cart store. */
import Cart from "cart-store";

/* Spec fields now carry imperial in brackets, e.g. "52 cm (20.5 in) by 10 m (32.8 ft) roll"; drop it before parsing. */
const stripImperial = (s) => String(s || "").replace(/\s*\(\s*\d+(?:[.,]\d+)?\s*(?:in|inch|inches|ft|feet|foot|"|'|′|″|”)\.?\s*\)/gi, "");
function parseRoll(text) {
  const t = stripImperial(text).trim();
  if (!t) return { mode: "unknown" };
  const low = t.toLowerCase();
  if (/panel/.test(low)) {
    const m = low.match(/(\d+(?:\.\d+)?)\s*cm\s*(?:w(?:ide)?)?\s*[x×]\s*(\d+(?:\.\d+)?)\s*(cm|m)\b/);
    const perSet = (low.match(/(\d+)\s*panels?\s*each/) || low.match(/contains\s*(\d+)\s*panels?/) || low.match(/comes in\s*(\d+)\s*panels?/) || [])[1];
    if (m) { let ph = parseFloat(m[2]); if (m[3] === "m") ph *= 100; return { mode: "panel", panelW: parseFloat(m[1]), panelH: ph, perSet: perSet ? parseInt(perSet, 10) : 1 }; }
    return { mode: "unknown" };
  }
  if (/running\s*met/.test(low)) { const w = low.match(/(\d+(?:\.\d+)?)\s*cm/); return w ? { mode: "metre", rollW: parseFloat(w[1]) } : { mode: "unknown" }; }
  const m = low.match(/(\d+(?:\.\d+)?)\s*cm.*?(\d+(?:\.\d+)?)\s*m\b/);
  if (m) return { mode: "roll", rollW: parseFloat(m[1]), rollL: parseFloat(m[2]) };
  const onlyLen = low.match(/(\d+(?:\.\d+)?)\s*m\s*roll/);
  if (onlyLen) return { mode: "roll", rollW: 52, rollL: parseFloat(onlyLen[1]), guessedWidth: true };
  return { mode: "unknown" };
}
function parseRepeat(text) {
  const t = stripImperial(text).trim(); if (!t) return null;
  const low = t.toLowerCase();
  if (/no\s*(repeat|match)|free\s*match/.test(low)) return 0;
  let m = low.match(/h\s*[:=]?\s*(\d+(?:\.\d+)?)\s*cm/);
  if (!m) m = low.match(/(\d+(?:\.\d+)?)\s*cm\s*(?:h\b|high|height|vertical)/);
  if (!m) m = low.match(/(?:vertical(?:\s*pattern)?\s*repeat\s*[:=]?\s*)(\d+(?:\.\d+)?)/);
  if (!m) m = low.match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*vertical/);
  if (!m) { const all = [...low.matchAll(/(\d+(?:\.\d+)?)\s*cm/g)]; if (all.length === 1) m = all[0]; }
  if (!m) { const bare = low.match(/^(\d+(?:\.\d+)?)\s*(cm)?$/); if (bare) m = bare; }
  return m ? parseFloat(m[1]) : null;
}
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

class WallpaperEstimator {
  constructor(el) {
    this.el = el; this.q = (s) => el.querySelector(s);
    this.in = Object.fromEntries([...el.querySelectorAll("[data-est]")].map((i) => [i.dataset.est, i]));
    this.result = this.q("[data-est-result]"); this.cta = this.q("[data-est-add]");
    this.unit = "cm";
    this.variants = JSON.parse(this.q("[data-variants]").textContent || "[]");
    this.sizeIdx = parseInt(el.dataset.sizeIndex, 10); this.colourIdx = parseInt(el.dataset.colourIndex, 10);
    this.formId = el.dataset.formId;
    this.spec = parseRoll(el.dataset.rollDimensions);
    this.applySpec(this.spec, parseRepeat(el.dataset.patternRepeat));
    el.querySelectorAll("[data-est]").forEach((i) => i.addEventListener("input", () => this.calc()));
    el.querySelectorAll("[data-est-w],[data-est-h]").forEach((i) => i.addEventListener("input", () => this.calc()));
    const addBtn = this.q("[data-est-add-wall]"); if (addBtn) addBtn.addEventListener("click", () => this.addWall());
    el.querySelectorAll("[data-est-wall-remove]").forEach((b) => b.addEventListener("click", (e) => this.removeWall(e.target.closest("[data-est-wall]"))));
    el.querySelectorAll("[data-unit]").forEach((b) => b.addEventListener("click", () => this.setUnit(b.dataset.unit)));
    this.q("[data-est-edit]").addEventListener("click", (e) => { e.preventDefault(); this.toggleEdit(); });
    this.q("[data-est-form]").addEventListener("submit", (e) => e.preventDefault());
    this.cta.addEventListener("click", () => this.addToCart());
    // Lock page scroll while the drawer is open (theme convention; modal-dialog.js removes the class on close).
    el.addEventListener("on:modal:opening", () => document.body.classList.add("u-scroll-disabled"));
    this.calc();
  }
  applySpec(spec, rep) {
    const rollFields = this.q("[data-est-roll-fields]"), panelFields = this.q("[data-est-panel-fields]"), auto = this.q("[data-est-auto-label]");
    if (spec.mode === "panel") {
      rollFields.hidden = true; panelFields.hidden = false;
      this.in.panelw.value = spec.panelW; this.in.panelh.value = spec.panelH; this.in.perset.value = spec.perSet;
      this.q("[data-est-match-wrap]").hidden = true;
      auto.textContent = "This design is sold in panels — filled in from the product";
    } else if (spec.mode === "roll" || spec.mode === "metre") {
      this.in.rollw.value = spec.rollW ?? ""; this.in.rolll.value = spec.mode === "metre" ? "" : (spec.rollL ?? "");
      if (spec.mode === "metre") this.in.rolll.placeholder = "sold by the metre";
      auto.textContent = spec.guessedWidth ? "Partly filled in — please check the roll width" : "Filled in from this product";
      if (spec.guessedWidth) this.toggleEdit(true);
    } else { auto.textContent = "You'll find these under Details on the product page"; this.toggleEdit(true); }
    if (rep === 0) { this.in.repeat.value = 0; this.in.match.value = "free"; }
    else if (rep != null) this.in.repeat.value = rep;
    else { this.in.repeat.value = ""; if (spec.mode !== "panel") this.toggleEdit(true); }
  }
  toggleEdit(force) {
    const fields = [this.in.rollw, this.in.rolll, this.in.repeat, this.in.match, this.in.panelw, this.in.panelh, this.in.perset].filter(Boolean);
    const unlock = force ?? fields[0].disabled;
    fields.forEach((f) => (f.disabled = !unlock));
    this.q("[data-est-edit]").textContent = unlock ? "Done" : "Edit";
  }
  setUnit(u) {
    if (u === this.unit) return;
    const f = u === "in" ? 1 / 2.54 : 2.54;
    this.el.querySelectorAll("[data-est-w],[data-est-h]").forEach((i) => {
      const v = parseFloat(i.value);
      if (v) i.value = Math.round(v * f);
      i.placeholder = i.dataset[u === "in" ? "phIn" : "phCm"] || i.placeholder;
    });
    this.unit = u;
    this.el.querySelectorAll("[data-unit]").forEach((b) => { const on = b.dataset.unit === u; b.classList.toggle("is-on", on); b.setAttribute("aria-pressed", String(on)); });
    this.calc();
  }
  cm(v) { return this.unit === "in" ? v * 2.54 : v; }
  wall(W, H) { return this.unit === "in" ? `${Math.round(W / 2.54)} × ${Math.round(H / 2.54)} in` : `${W.toFixed(0)} × ${H.toFixed(0)} cm`; }

  readWalls() {
    const walls = [];
    this.el.querySelectorAll("[data-est-wall]").forEach((r) => {
      if (r.hidden) return;
      const W = this.cm(parseFloat(r.querySelector("[data-est-w]").value) || 0);
      const H = this.cm(parseFloat(r.querySelector("[data-est-h]").value) || 0);
      if (W > 0 && H > 0) walls.push({ W, H });
    });
    return walls;
  }
  addWall() {
    const rows = [...this.el.querySelectorAll("[data-est-wall]")];
    const next = rows.find((r) => r.hidden);
    if (next) { next.hidden = false; const w = next.querySelector("[data-est-w]"); if (w) w.focus(); }
    const addBtn = this.q("[data-est-add-wall]");
    if (addBtn && !rows.some((r) => r.hidden)) addBtn.hidden = true;
  }
  removeWall(row) {
    if (!row) return;
    row.hidden = true;
    row.querySelectorAll("input").forEach((i) => (i.value = ""));
    const addBtn = this.q("[data-est-add-wall]"); if (addBtn) addBtn.hidden = false;
    this.calc();
  }

  calc() {
    const walls = this.readWalls();
    this.rec = null; this.cta.disabled = true; this.cta.textContent = "Add to cart";
    if (!walls.length) { this.result.innerHTML = ""; return; }
    const totalW = walls.reduce((s, w) => s + w.W, 0);
    const Hmax = Math.max(...walls.map((w) => w.H));
    const multi = walls.length > 1;
    const wallLabel = multi ? `${walls.length} walls (${totalW.toFixed(0)} cm total)` : this.wall(walls[0].W, walls[0].H);
    if (this.spec.mode === "panel") return this.calcPanels(walls, totalW, Hmax, multi, wallLabel);
    const RW = parseFloat(this.in.rollw.value) || 0, RLm = parseFloat(this.in.rolll.value) || 0, RP = parseFloat(this.in.repeat.value) || 0, M = this.in.match.value;
    if (!RW) return this.err("We just need the roll width to work this out — you'll find it under Details on the product page, or send us your wall sizes and we'll happily help.");
    let drop = Hmax + 10;
    if (M !== "free" && RP > 0) { drop = Math.ceil(drop / RP) * RP; if (M === "half") drop += RP / 2; }
    const perWall = walls.map((w) => Math.ceil(w.W / RW));
    const drops = perWall.reduce((a, b) => a + b, 0);
    const repeatNote = RP && M !== "free" ? ` (rounded up to whole ${RP} cm repeats${M === "half" ? ", plus a half for the offset" : ""})` : "";
    const dropsLine = multi
      ? `Drops per wall: ${walls.map((w, i) => `${w.W.toFixed(0)} cm → ${perWall[i]}`).join(" · ")}  (total ${drops})`
      : `Drops across the wall: ${totalW.toFixed(0)} ÷ ${RW} = ${drops}`;
    const heightNote = multi ? `tallest wall ${Hmax.toFixed(0)} cm` : `${walls[0].H.toFixed(0)} cm`;

    if (this.spec.mode === "metre" || !RLm) {
      const metresExact = (drops * drop) / 100;
      const recM = Math.ceil(metresExact);
      this.rec = { qty: recM, label: "metre" };
      this.render(`${recM}`, `m of ${RW} cm-wide paper for ${wallLabel}`, `${metresExact.toFixed(1)} m needed`,
        `That's the length your ${multi ? "walls" : "wall"} need, rounded up to the next whole metre. Ordering it in one go keeps the colour matched across the run.`,
        `Each drop: ${heightNote} + 10 cm for trimming → ${drop.toFixed(1)} cm${repeatNote}\n${dropsLine}\nTotal length: ${drops} × ${drop.toFixed(1)} cm = ${metresExact.toFixed(1)} m → ${recM} m`);
      this.cta.textContent = `Add ${recM} m to cart`; this.cta.disabled = false; return;
    }
    const RL = RLm * 100, perRoll = Math.floor(RL / drop);
    if (perRoll < 1) return this.err(`It looks like your wall is taller than one roll of this paper (${RLm} m), so we can't estimate it automatically. Send us your measurements and we'll happily work it out for you.`);
    const rollsExact = drops / perRoll;
    const rec = Math.ceil(rollsExact);
    const whole = Number.isInteger(rollsExact);
    const exactStr = whole ? `${rec}` : rollsExact.toFixed(2);
    this.rec = { qty: rec, label: "roll" };
    this.render(`${rec}`, `${rec === 1 ? "roll" : "rolls"} for ${wallLabel}`,
      whole ? "exact fit" : `${exactStr} rolls`,
      whole
        ? `Your ${multi ? "walls come" : "wall comes"} to exactly ${plural(rec, "roll")} — no spare needed. Add one more only if you'd like a little in hand.`
        : `${exactStr} rolls rounds up to ${plural(rec, "roll")}. Ordering them together means they all come from one print run, so the colour matches throughout.`,
      `Each drop: ${heightNote} + 10 cm for trimming → ${drop.toFixed(1)} cm${repeatNote}\n${dropsLine}\nDrops from one roll: ${RLm} m ÷ ${drop.toFixed(1)} cm = ${perRoll}\nRolls: ${drops} ÷ ${perRoll} = ${exactStr}${whole ? "" : ` → rounded up to ${rec}`}`);
    this.cta.textContent = `Add ${plural(rec, "roll")} to cart`; this.cta.disabled = false;
  }
  calcPanels(walls, totalW, Hmax, multi, wallLabel) {
    const PW = parseFloat(this.in.panelw.value) || 0, PH = parseFloat(this.in.panelh.value) || 0, PS = parseInt(this.in.perset.value, 10) || 1;
    if (!PW || !PH) return this.err("We just need the panel size to work this out — you'll find it under Details on the product page.");
    if (Hmax > PH) return this.err(`Your wall (${Hmax.toFixed(0)} cm) is a little taller than this panel (${PH.toFixed(0)} cm). Send us your measurements and we'll happily advise on the best way to fit it.`);
    const perWall = walls.map((w) => Math.ceil(w.W / PW));
    const panels = perWall.reduce((a, b) => a + b, 0);
    const sets = Math.ceil(panels / PS);
    this.rec = { qty: sets, label: PS > 1 ? "set" : "panel" };
    const unitTxt = PS > 1 ? `${sets === 1 ? "set" : "sets"} of ${PS} panels` : (sets === 1 ? "panel" : "panels");
    const panelsLine = multi ? `Panels per wall: ${perWall.join(" + ")} = ${panels}` : `Panels across the wall: ${totalW.toFixed(0)} ÷ ${PW} = ${panels}`;
    this.render(`${sets}`, `${unitTxt} for ${wallLabel}`, `${plural(panels, "panel")} across`,
      `Each panel is ${PW} × ${PH} cm. Ordering everything together keeps it all from one print run.`,
      `${panelsLine}${PS > 1 ? `\nSets: ${panels} ÷ ${PS} per set = ${sets}` : ""}\nHeight: your ${Hmax.toFixed(0)} cm wall fits within the ${PH} cm panel`);
    this.cta.textContent = `Add ${sets} to cart`; this.cta.disabled = false;
  }
  render(big, what, pill, recText, working) {
    this.result.innerHTML = `<div class="wallpaper-estimator__card"><span class="wallpaper-estimator__big">${big}</span><span class="wallpaper-estimator__what">${what}</span>
      <div class="wallpaper-estimator__rec"><span class="wallpaper-estimator__pill">${pill}</span><span>${recText}</span></div></div>
      <p class="wallpaper-estimator__working-label">How we worked it out</p><p class="wallpaper-estimator__working">${working}</p>`;
  }
  err(msg) { this.result.innerHTML = `<div class="wallpaper-estimator__err">${msg}</div>`; this.rec = null; this.cta.disabled = true; }
  targetVariant() {
    const form = document.getElementById(this.formId);
    const curId = form ? parseInt(form.querySelector('input[name="id"]')?.value, 10) : null;
    const cur = this.variants.find((v) => v.id === curId) || this.variants[0];
    const isSample = (v) => this.sizeIdx >= 0 && /sample/i.test(v.options[this.sizeIdx] || "");
    if (cur && !isSample(cur)) return cur;
    const sameColour = (v) => this.colourIdx < 0 || !cur || v.options[this.colourIdx] === cur.options[this.colourIdx];
    return this.variants.find((v) => !isSample(v) && sameColour(v) && v.available) || this.variants.find((v) => !isSample(v) && sameColour(v)) || null;
  }
  async addToCart() {
    if (!this.rec) return;
    const v = this.targetVariant();
    if (!v) return this.err("We couldn't find a full roll in this colour — please choose it on the product page and add it to your cart from there.");
    const btn = this.cta, txt = btn.textContent; btn.disabled = true; btn.textContent = "Adding…";
    try {
      await Cart.add({ id: v.id, quantity: this.rec.qty });
      btn.textContent = "Added";
      const cart = document.querySelector(".modal--cart, .popup--cart-notification");
      await this.el.close?.();
      if (cart && !cart.shown && typeof cart.open === "function") cart.open(); else if (!cart) window.location.href = "/cart";
    } catch (e) { this.err(e?.message || "Sorry — that didn't quite add to your cart. Please try from the product page."); btn.textContent = txt; }
    finally { setTimeout(() => { btn.disabled = false; btn.textContent = txt; }, 1200); }
  }
}
function boot() { document.querySelectorAll("modal-dialog.wallpaper-estimator:not([data-est-ready])").forEach((el) => { el.dataset.estReady = "1"; new WallpaperEstimator(el); }); }
boot();
document.addEventListener("click", (e) => {
  const a = e.target.closest('a[href$="#wallpaper-estimator"]');
  if (!a || a.hasAttribute("data-a11y-dialog-show")) return;
  const el = document.getElementById("wallpaper-estimator");
  if (el && typeof el.open === "function") { e.preventDefault(); el.open(); }
});
